import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../components/ThemeToggle';
import LanguageSelector from '../components/LanguageSelector';
import { Eye, EyeOff, ArrowRight, AlertCircle, ArrowLeft, Building2, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

const CARE_IMAGES = [
  { url: "https://images.pexels.com/photos/18459702/pexels-photo-18459702.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Compassionate elderly care", label: "Elderly Care" },
  { url: "https://images.unsplash.com/photo-1765896387398-1e1ae8d2eb85?fit=crop&crop=faces&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900&h=1400", caption: "Building meaningful connections", label: "Senior Support" },
  { url: "https://images.pexels.com/photos/6284845/pexels-photo-6284845.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Supporting independence every day", label: "Disability Care" },
  { url: "https://images.pexels.com/photos/8415495/pexels-photo-8415495.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Empowering mobility and freedom", label: "Accessibility Support" },
  { url: "https://images.pexels.com/photos/27177471/pexels-photo-27177471.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Nurturing the next generation", label: "Childcare" },
  { url: "https://images.pexels.com/photos/4017421/pexels-photo-4017421.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800", caption: "Joyful moments in early learning", label: "Early Years" },
];

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountChoices, setAccountChoices] = useState(null);
  
  const [currentImageIdx, setCurrentImageIdx] = useState(() => Math.floor(Math.random() * CARE_IMAGES.length));

  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIdx(prev => (prev + 1) % CARE_IMAGES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      const role = result.user?.role;
      const onboardingKey = `onboarding_completed_${role}`;
      if (role && !localStorage.getItem(onboardingKey)) {
        navigate('/guide?auto=true', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else if (result.multiple_accounts) {
      setAccountChoices(result.accounts);
      setError('');
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  const selectAccount = async (accountId) => {
    setIsLoading(true);
    setError('');
    const result = await login(email, password, accountId);
    if (result.success) {
      const role = result.user?.role;
      const onboardingKey = `onboarding_completed_${role}`;
      if (role && !localStorage.getItem(onboardingKey)) {
        navigate('/guide?auto=true', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      setError(result.error || 'Failed to select account');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Header with back link */}
          <div className="flex items-center justify-between gap-2 mb-8 flex-wrap">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="FendanaCura" className="h-8 sm:h-10 w-auto" />
              <span className="text-lg sm:text-xl font-heading font-bold text-foreground">FendanaCura</span>
            </Link>
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <ThemeToggle />
            </div>
          </div>

          {/* Back to homepage */}
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-home-link">
            <ArrowLeft className="w-4 h-4" />
            Back to homepage
          </Link>

          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
              Sign in to your account
            </h1>
            <p className="text-muted-foreground">
              Welcome back — enter your credentials below
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          {accountChoices ? (
            <div className="space-y-4" data-testid="account-selector">
              <div>
                <h2 className="text-lg font-heading font-semibold text-foreground">Select an Account</h2>
                <p className="text-sm text-muted-foreground mt-1">Your email is registered with multiple organisations. Choose which one to sign into:</p>
              </div>
              <div className="space-y-2">
                {accountChoices.map(acc => (
                  <button
                    key={acc.account_id}
                    onClick={() => selectAccount(acc.account_id)}
                    disabled={isLoading}
                    className="w-full p-4 rounded-xl border border-border bg-card hover:border-[#00A6A0]/50 hover:bg-[#00A6A0]/5 transition-all flex items-center justify-between group text-left"
                    data-testid={`account-option-${acc.account_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-muted-foreground group-hover:text-[#00A6A0]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{acc.organization_name || 'Personal Account'}</p>
                        <p className="text-xs text-muted-foreground capitalize">{acc.role?.replace('_', ' ')} {acc.cisc ? `· ${acc.cisc}` : ''}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#00A6A0]" />
                  </button>
                ))}
              </div>
              <Button variant="ghost" className="text-sm text-muted-foreground" onClick={() => { setAccountChoices(null); setError(''); }}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to login
              </Button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-card border-border"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="bg-card border-border pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-6"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="spinner w-4 h-4" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>
          )}

          {/* Google Sign In */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-background text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={loginWithGoogle}
              className="w-full mt-4 btn-secondary py-6"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>
          </div>

          {/* Sign Up / Register links */}
          <div className="mt-8 text-center space-y-3">
            <p className="text-muted-foreground text-sm">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#00A6A0] hover:underline font-medium" data-testid="sign-up-link">
                Sign up
              </Link>
            </p>
            <p className="text-muted-foreground text-sm">
              Have an invitation token?{' '}
              <Link to="/invite" className="text-[#4A90E2] hover:underline font-medium" data-testid="invitation-link">
                Use invitation token
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Rotating Care Images */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden" data-testid="auth-image-panel">
        {CARE_IMAGES.map((img, idx) => (
          <div
            key={idx}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: idx === currentImageIdx ? 1 : 0 }}
          >
            <img
              src={img.url}
              alt={img.caption}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-background/30 to-background" />
          </div>
        ))}
        <div className="absolute bottom-12 left-12 right-12 p-8 rounded-2xl glass-effect border border-border z-20">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-[#00A6A0]/20 text-[#00A6A0] mb-3">
            {CARE_IMAGES[currentImageIdx].label}
          </span>
          <p className="text-lg text-foreground font-medium mb-2">
            {CARE_IMAGES[currentImageIdx].caption}
          </p>
          <p className="text-muted-foreground text-sm">
            "FendanaCura transformed how we manage our care operations."
          </p>
        </div>
        {/* Image dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {CARE_IMAGES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentImageIdx(idx)}
              className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIdx ? 'bg-[#00A6A0] w-6' : 'bg-white/40'}`}
              aria-label={`Show image ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default Login;
